import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertTriangle, Search, Plus, Eye, Edit, Save, X } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const IncidentReportPage = () => {
  const [reports, setReports] = useState([]);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', incidentDate: '', incidentTime: '', location: '', incidentType: '',
    description: '', injuryOccurred: false, injuryDescription: '', actionsTaken: '', reportedBy: '', witnessName: ''
  });

  useEffect(() => {
    fetchReports();
    fetchPatients();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get('/incident-reports');
      if (response.data.success) {
        setReports(response.data.reports);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      if (response.data.success) {
        setPatients(response.data.patients);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.patientId || !formData.incidentType || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }
    const patient = patients.find(p => p.id === formData.patientId);
    try {
      await api.post('/incident-reports', { ...formData, patientName: `${patient.firstName} ${patient.lastName}` });
      toast.success('Incident report submitted');
      fetchReports();
      resetForm();
    } catch (error) {
      toast.error('Error creating incident report');
    }
  };

  const handleClose = async (reportId) => {
    try {
      await api.put(`/incident-reports/${reportId}/close`);
      toast.success('Incident report closed');
      fetchReports();
    } catch (error) {
      toast.error('Error closing report');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setFormData({
      patientId: '', patientName: '', incidentDate: '', incidentTime: '', location: '', incidentType: '',
      description: '', injuryOccurred: false, injuryDescription: '', actionsTaken: '', reportedBy: '', witnessName: ''
    });
  };

  const filteredReports = reports.filter(report =>
    report.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.incidentType?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const incidentTypes = ['Fall', 'Medication Error', 'Equipment Failure', 'Patient Complaint', 'Injury', 'Near Miss', 'Skin Breakdown', 'Other'];
  const underReviewCount = reports.filter(r => r.status === 'Under Review').length;
  const closedCount = reports.filter(r => r.status === 'Closed').length;

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                Incident Report
              </h1>
              <p className="text-gray-500 text-sm mt-1">Document and track patient safety incidents</p>
            </div>
            <Button onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-white">
              <Plus className="w-4 h-4 mr-2" />New Incident Report
            </Button>
          </div>
        </div>

        {showForm ? (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                New Incident Report
              </h2>
              <Button variant="ghost" onClick={resetForm}><X className="w-5 h-5" /></Button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label>Patient *</Label>
                <select className="w-full mt-1 p-2 border rounded" value={formData.patientId} onChange={(e) => handleChange('patientId', e.target.value)}>
                  <option value="">Select Patient</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                </select>
              </div>
              <div>
                <Label>Incident Type *</Label>
                <select className="w-full mt-1 p-2 border rounded" value={formData.incidentType} onChange={(e) => handleChange('incidentType', e.target.value)}>
                  <option value="">Select Type</option>
                  {incidentTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div>
                <Label>Date of Incident *</Label>
                <Input type="date" value={formData.incidentDate} onChange={(e) => handleChange('incidentDate', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Time of Incident</Label>
                <Input type="time" value={formData.incidentTime} onChange={(e) => handleChange('incidentTime', e.target.value)} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label>Location</Label>
                <Input value={formData.location} onChange={(e) => handleChange('location', e.target.value)} placeholder="Where did the incident occur?" className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label>Description of Incident *</Label>
                <textarea value={formData.description} onChange={(e) => handleChange('description', e.target.value)} className="w-full mt-1 p-3 border rounded-md" rows={4} placeholder="Describe what happened..." />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox checked={formData.injuryOccurred} onCheckedChange={(c) => handleChange('injuryOccurred', c)} />
                <Label>Injury occurred as a result of this incident</Label>
              </div>
              {formData.injuryOccurred && (
                <div className="col-span-2">
                  <Label>Describe Injury</Label>
                  <textarea value={formData.injuryDescription} onChange={(e) => handleChange('injuryDescription', e.target.value)} className="w-full mt-1 p-3 border rounded-md" rows={2} />
                </div>
              )}
              <div className="col-span-2">
                <Label>Actions Taken *</Label>
                <textarea value={formData.actionsTaken} onChange={(e) => handleChange('actionsTaken', e.target.value)} className="w-full mt-1 p-3 border rounded-md" rows={3} placeholder="What actions were taken?" />
              </div>
              <div>
                <Label>Reported By *</Label>
                <Input value={formData.reportedBy} onChange={(e) => handleChange('reportedBy', e.target.value)} className="mt-1" placeholder="Your name" />
              </div>
              <div>
                <Label>Witness Name (if any)</Label>
                <Input value={formData.witnessName} onChange={(e) => handleChange('witnessName', e.target.value)} className="mt-1" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700 text-white">
                <Save className="w-4 h-4 mr-2" />Submit Report
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-2xl font-bold text-red-700">{underReviewCount}</div>
                <div className="text-sm text-red-600">Under Review</div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-700">{closedCount}</div>
                <div className="text-sm text-green-600">Closed</div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="text-2xl font-bold text-yellow-700">{reports.length}</div>
                <div className="text-sm text-yellow-600">Total Reports</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-700">0</div>
                <div className="text-sm text-blue-600">Pending Follow-up</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search incidents..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reported By</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
                  ) : filteredReports.length === 0 ? (
                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No incident reports found</td></tr>
                  ) : filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{report.patientName}</td>
                      <td className="px-6 py-4">{report.incidentType}</td>
                      <td className="px-6 py-4">{report.incidentDate}</td>
                      <td className="px-6 py-4">{report.reportedBy}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${report.status === 'Closed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {report.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                          {report.status !== 'Closed' && (
                            <Button variant="ghost" size="sm" onClick={() => handleClose(report.id)} className="text-green-600">Close</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default IncidentReportPage;
